/**
 * SettingsLandingPage Component
 *
 * Card-based landing page for Settings navigation.
 * Displays all settings categories as interactive cards for discovery and navigation.
 */

// 1. External dependencies
import React from 'react';
import {
  SlidersHorizontal,
  Layers,
  Target,
  Briefcase,
  Cloud,
  Brain,
  MessageCircleQuestion,
  User,
  ArrowRight,
  type LucideIcon
} from 'lucide-react';

// 2. Internal absolute imports
import { Card, CardHeader, CardBody, CardFooter } from '@enfomentis/ui';

// 3. Relative imports
import { settingsSections, type SettingsSectionKey } from './settingsData';

/** Icon mapping for each settings section */
const sectionIcons: Record<SettingsSectionKey, LucideIcon> = {
  'preferences': SlidersHorizontal,
  'workflow': Layers,
  'strategies': Target,
  'portfolio': Briefcase,
  'data-sync': Cloud,
  'ai': Brain,
  'feedback-support': MessageCircleQuestion,
  'profile': User
};

export interface SettingsLandingPageProps {
  /** Callback when user selects a settings section */
  onNavigateToSection: (sectionKey: SettingsSectionKey) => void;
}

/**
 * Settings Landing Page
 *
 * Provides card-based navigation to different settings sections.
 * Uses progressive disclosure pattern - click card to access full settings.
 */
export const SettingsLandingPage: React.FC<SettingsLandingPageProps> = ({
  onNavigateToSection
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {settingsSections.map((section) => {
          const Icon = sectionIcons[section.key];

          return (
            <Card
              key={section.key}
              isPressable
              isHoverable
              className="h-full hover:scale-[1.02] transition-transform cursor-pointer"
              onPress={() => onNavigateToSection(section.key)}
            >
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="text-primary">
                    <Icon size={24} />
                  </div>
                  <h3 className="pl-2 text-lg font-semibold text-text-primary">
                    {section.title}
                  </h3>
                </div>
              </CardHeader>
              <CardBody className="flex items-center text-small text-text-primary">  
                {section.description}
              </CardBody>
              <CardFooter className="justify-end pt-3">
                <div className="flex items-center gap-2 text-primary text-sm font-medium">
                  <span>Configure</span>
                  <ArrowRight size={16} />
                </div>
              </CardFooter>
            </Card>
          );
        })}
    </div>
  );
};
